import { useCallback, useState } from "react";
import {
  ApiResponse,
  RegisterResponseDto,
  LoginRequestDto,
  ForgotPasswordRequestDto,
  ResetPasswordRequestDto,
  ApiDataResponse,
  AppUser,
  RegistrationDto,
  UpdatePasswordRequestDto,
  ActivateAccountRequestDto,
} from "../services/Dtos";
import useAxios from "./useAxios";
import { useApiResponseHandler } from "./useApiResponseHandler";
import { useUserContext } from "../Contexts/userContext";
import { useNavigate } from "react-router-dom";
import useSound from "use-sound";

const useAuth = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setErrors] = useState<string[]>([]);
  const axiosClient = useAxios();
  const { setUser } = useUserContext();
  const { handleApiError, handleApiSuccess, clearErrors } =
    useApiResponseHandler({
      setErrors,
    });
  const navigate = useNavigate();
  const [play] = useSound("../../../../welcome-sound-MP3.mp3", {
    volume: 0.2,
  });

  const register = useCallback(
    async (
      registrationData: RegistrationDto
    ): Promise<ApiDataResponse<RegisterResponseDto>> => {
      setLoading(true);
      clearErrors();
      try {
        const response = await axiosClient.post<
          ApiDataResponse<RegisterResponseDto>
        >("authentication/register", registrationData);
        if (response.data.succeeded) {
          const loginResult = await login({
            username: registrationData.email,
            password: registrationData.password,
          });

          if (loginResult?.succeeded) {
            localStorage.setItem("token", loginResult?.data.token);
            localStorage.setItem("user", JSON.stringify(loginResult?.data));

            setUser(loginResult.data);
            handleApiSuccess("Registration and login successful!");
            // Redirect to dashboard
            navigate("/");
            play();
          } else {
            navigate("/auth/login");
          }
        } else {
          handleApiError(response.data, "Registration failed");
          return response.data;
        }
        return response.data;
      } catch (err: any) {
        const errorMessage = handleApiError(
          err,
          "Failed to complete registration"
        );
        return {
          succeeded: false,
          message: errorMessage?.message || "Failed to complete registration",
          statusCode: 500,
          errors: err.response?.data.errors || [],
          data: {} as RegisterResponseDto,
        };
      } finally {
        setLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  const login = useCallback(
    async (
      loginRequest: LoginRequestDto
    ): Promise<ApiDataResponse<AppUser>> => {
      setLoading(true);
      clearErrors();
      try {
        const response = await axiosClient.post<ApiDataResponse<AppUser>>(
          "authentication/login",
          loginRequest
        );
        if (response.data.succeeded) {
          localStorage.setItem("token", response.data.data.token);
          localStorage.setItem("user", JSON.stringify(response.data.data));
          setUser(response.data.data);
          handleApiSuccess("Login successful!");
          navigate("/");
          play();
          return response.data;
        } else {
          handleApiError(response.data, "Login failed");
          return response.data;
        }
      } catch (err: any) {
        const errorMessage = handleApiError(err, "Login failed");
        return {
          succeeded: false,
          message: errorMessage?.message || "Login failed",
          statusCode: 500,
          errors: err.response?.data.errors || [],
          data: {} as AppUser,
        };
      } finally {
        setLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  const forgotPassword = useCallback(
    async (
      forgotPasswordRequest: ForgotPasswordRequestDto
    ): Promise<ApiResponse> => {
      setLoading(true);
      clearErrors();
      try {
        const response = await axiosClient.post<ApiResponse>(
          "authentication/forgotpassword",
          forgotPasswordRequest
        );
        if (response.data.succeeded) {
          handleApiSuccess("Password reset link sent to your email!");
          return response.data;
        } else {
          handleApiError(response.data, "Failed to send reset link");
          return response.data;
        }
      } catch (err: any) {
        const errorMessage = handleApiError(err, "Failed to send reset link");
        return {
          succeeded: false,
          message: errorMessage?.message || "Failed to send reset link",
          statusCode: 500,
          errors: err.response?.data.errors || [],
        };
      } finally {
        setLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  const resetPassword = useCallback(
    async (
      resetPasswordRequest: ResetPasswordRequestDto
    ): Promise<ApiResponse> => {
      setLoading(true);
      setErrors([]);
      try {
        const response = await axiosClient.post<ApiResponse>(
          "authentication/resetpassword",
          resetPasswordRequest
        );
        if (response.data.succeeded) {
          return response.data;
        } else {
          handleApiError(response.data, "Request Failed");
          return response.data;
        }
      } catch (err: any) {
        const errorMessage = handleApiError(err, "Request Failed");
        return {
          succeeded: false,
          message: errorMessage?.message || "Request Failed",
          statusCode: 500,
          errors: err.response?.data.errors || [],
        };
      } finally {
        setLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  const activateAccount = useCallback(
    async (
      activateAccountRequest: ActivateAccountRequestDto
    ): Promise<ApiResponse> => {
      setLoading(true);
      setErrors([]);
      try {
        const response = await axiosClient.post<ApiResponse>(
          "authentication/activateaccount",
          activateAccountRequest
        );
        if (response.data.succeeded) {
          return response.data;
        } else {
          handleApiError(response.data, "Request Failed");
          return response.data;
        }
      } catch (err: any) {
        const errorMessage = handleApiError(err, "Request Failed");
        return {
          succeeded: false,
          message: errorMessage?.message || "Request Failed",
          statusCode: 500,
          errors: err.response?.data.errors || [],
        };
      } finally {
        setLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  const refreshToken = useCallback(async () => {
    setLoading(true);
    clearErrors();
    try {
      const response = await axiosClient.post<ApiDataResponse<AppUser>>(
        "authentication/refreshtoken"
      );
      if (response.data.succeeded) {
        return response.data;
      } else {
        handleApiError(response.data, "Request Failed");
        return response.data;
      }
    } catch (err: any) {
      const errorMessage = handleApiError(err, "Request Failed");
      return {
        succeeded: false,
        message: errorMessage?.message || "Request Failed",
        statusCode: 500,
        errors: err.response?.data.errors || [],
      };
    } finally {
      setLoading(false);
    }
  }, [axiosClient, handleApiError, handleApiSuccess, clearErrors]);

  const updatePassword = useCallback(
    async (
      updatePasswordRequest: UpdatePasswordRequestDto
    ): Promise<ApiResponse> => {
      setLoading(true);
      clearErrors();
      try {
        const response = await axiosClient.post<ApiResponse>(
          "authentication/updatepassword",
          updatePasswordRequest
        );
        if (response.data.succeeded) {
          handleApiSuccess("Password updated successfully!");
          return response.data;
        } else {
          handleApiError(response.data, "Failed to update password");
          return response.data;
        }
      } catch (err: any) {
        const errorMessage = handleApiError(err, "Failed to update password");
        return {
          succeeded: false,
          message: errorMessage?.message || "Failed to update password",
          statusCode: 500,
          errors: err.response?.data.errors || [],
        };
      } finally {
        setLoading(false);
      }
    },
    [axiosClient, handleApiError, handleApiSuccess, clearErrors]
  );

  const logout = useCallback(async () => {
    try {
      await axiosClient.post("authentication/logout");
      setUser(null);
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      navigate("/auth/login");
      handleApiSuccess("You are logged out");
    } catch (error) {
      handleApiError(error, "Failed to logout");
    }
  }, [axiosClient, setUser, navigate]);

  return {
    register,
    login,
    forgotPassword,
    resetPassword,
    activateAccount,
    refreshToken,
    updatePassword,
    logout,
    loading,
    error,
  };
};

export default useAuth;
